import { useState, type FormEvent } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/services/http";

export default function PortalLoginPage() {
  const { login, user, isAuthenticated, isLoading, isDemoMode } = useAuth();
  const navigate = useNavigate();

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Already signed in — send admins to the console, everyone else to the portal.
  if (!isLoading && isAuthenticated && user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const signedIn = await login(email, password);
      navigate(signedIn.role === "admin" ? "/admin" : "/account", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Unable to sign in. Please check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-primary-900 p-12 lg:flex lg:w-[420px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Karnataka%20coastal%20town%20with%20fishing%20harbour%2C%20Arabian%20Sea%20waves%2C%20lush%20green%20hills%20and%20traditional%20buildings%20at%20golden%20hour%2C%20cinematic%20travel%20photography&width=840&height=1200&seq=portal-login-bg-01&orientation=portrait"
            alt=""
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/80 via-primary-900/60 to-primary-900/90" />
        </div>

        <div className="relative">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-400">
              <i className="ri-building-2-line text-xl text-foreground-950" />
            </span>
            <div>
              <p className="text-sm font-bold text-background-50">Coastal Bizz Connect</p>
              <p className="text-[11px] text-background-400">Owner Portal</p>
            </div>
          </Link>
        </div>

        <div className="relative space-y-6">
          <h1 className="font-heading text-3xl font-bold leading-tight text-background-50">
            Grow your business on Karnataka's coastal directory.
          </h1>
          <div className="space-y-3">
            {[
              { icon: "ri-bar-chart-2-line", text: "Track every lead and enquiry in one place" },
              { icon: "ri-verified-badge-line", text: "Manage your listing, hours and gallery" },
              { icon: "ri-notification-3-line", text: "Get instant alerts when customers reach out" },
            ].map((item) => (
              <div key={item.icon} className="flex items-center gap-3 text-sm text-background-300">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-50/10">
                  <i className={`${item.icon} text-accent-400 text-sm`} />
                </span>
                {item.text}
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-xs text-background-500">
          © {new Date().getFullYear()} Coastal Bizz Connect. All rights reserved.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-background-50 px-6 py-12">
        <div className="w-full max-w-sm">
          {isDemoMode && (
            <div className="mb-6 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-accent-800">
                <i className="ri-information-line" /> Demo Mode
              </p>
              <p className="mt-1 text-accent-700">
                Backend not connected — enter any credentials to explore the portal.
              </p>
            </div>
          )}

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">Welcome back</h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              Sign in to your owner account to manage your listing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className={labelClass} htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className={labelClass} htmlFor="login-password">Password</label>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPass ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-700"
                >
                  <i className={showPass ? "ri-eye-off-line text-base" : "ri-eye-line text-base"} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-base" /> Signing in…</>
              ) : (
                <><i className="ri-login-box-line text-base" /> Sign In</>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-600">
            New to the portal?{" "}
            <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700">
              Create your account
            </Link>
          </p>

          <div className="mt-8 border-t border-background-200 pt-6">
            <Link to="/listing-plans" className="flex items-center justify-center gap-2 text-xs text-foreground-500 hover:text-foreground-800">
              <i className="ri-price-tag-3-line text-sm" />
              View listing plans &amp; pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}